import type { BodyId, ScaleMode } from '../core/types';
import { BODY_IDS } from '../core/reference-data';
import { MIN_SECTOR_COUNT, MAX_SECTOR_COUNT } from '../core/sectors';
import {
  ECCENTRICITY_MIN,
  ECCENTRICITY_MAX,
  SEMI_MAJOR_AXIS_MIN_METRES,
  SEMI_MAJOR_AXIS_MAX_METRES,
  SPEED_MIN_DAYS_PER_SECOND,
  SPEED_MAX_DAYS_PER_SECOND
} from '../core/ranges';

/**
 * The versioned share codec (AD-4). A PURE `core`-only leaf: NO DOM, no `location`,
 * no `window`, no `state`/`ui` imports. It imports ONLY from `../core/*`. It is the
 * only serializer of the shareable experiment; the `AppState <-> ShareableConfig`
 * projection and the sole URL read/write live in `app` (`src/app/share-state.ts`,
 * `src/app/main.ts`), never here.
 *
 * Wire format (frozen once shipped, see the story "Saved questions"): the fragment
 * is `` `${SHARE_SCHEMA_VERSION}:${base64url(compactJson)}` ``. The version prefix
 * sits OUTSIDE the blob so `decode` can reject/migrate an unknown version WITHOUT
 * decoding the payload (AD-4). The compact JSON uses short keys and canonical-order
 * arrays; `a` is stored in VERBATIM SI metres (an f64 JSON-round-trips exactly in
 * JS, guaranteeing AC-4 strict equality) rather than rounded AU.
 */

/** The current schema version. Bump ONLY on a breaking wire-format change. */
export const SHARE_SCHEMA_VERSION = 1;

/**
 * The exact shareable include-set (AD-4). Per-body live `a` (SI metres) and `e`, in
 * canonical `BODY_IDS` order, plus the discrete experiment settings and the schema
 * version (carried by the fragment prefix, not this object). NOTHING else is here:
 * animation time, dragged phase, body status, camera, selection, play/pause, and
 * all graphics prefs are excluded and never serialized.
 */
export interface ShareableConfig {
  centralBody: BodyId;
  bodies: Record<BodyId, { a: number; e: number }>;
  legendOrder: BodyId[];
  sectorOverlayVisible: boolean;
  sectorCount: number;
  speedDaysPerSecond: number;
  scaleMode: ScaleMode;
}

/** A typed decode outcome; `decode` NEVER throws (AD-4 startup-never-breaks). */
export type DecodeResult =
  | { ok: true; config: ShareableConfig }
  | { ok: false; reason: 'unsupported-version' | 'malformed' };

/** Compact on-wire JSON shape (short keys). Internal to the codec. */
interface WirePayload {
  /** centralBody as a BODY_IDS index. */
  c: number;
  /** per-body semi-major axis, SI metres, canonical order. */
  a: number[];
  /** per-body eccentricity, canonical order. */
  e: number[];
  /** legend order as BODY_IDS indices. */
  l: number[];
  /** sectorOverlayVisible as 0/1. */
  so: number;
  /** sectorCount. */
  sc: number;
  /** speedDaysPerSecond. */
  sp: number;
  /** scaleMode as 0=readable / 1=realistic. */
  sm: number;
}

const SCALE_MODES: readonly ScaleMode[] = ['readable', 'realistic'];

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** UTF-8-safe base64url encode of a string (native `btoa`, bundled/no CDN, AD-6). */
function toBase64Url(json: string): string {
  // The compact JSON is pure ASCII (ids/scaleMode are ASCII, values are numbers/
  // booleans), so `btoa` is safe directly. The +/=/ transforms make it URL-safe.
  return btoa(json).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url decode back to a string; throws on invalid input (caught by decode). */
function fromBase64Url(b64url: string): string {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  return atob(padded);
}

/**
 * Serialize a shareable config to a URL fragment string (WITHOUT a leading `#`).
 * Pure and deterministic.
 */
export function encode(config: ShareableConfig): string {
  const payload: WirePayload = {
    c: BODY_IDS.indexOf(config.centralBody),
    a: BODY_IDS.map((id) => config.bodies[id].a),
    e: BODY_IDS.map((id) => config.bodies[id].e),
    l: config.legendOrder.map((id) => BODY_IDS.indexOf(id)),
    so: config.sectorOverlayVisible ? 1 : 0,
    sc: config.sectorCount,
    sp: config.speedDaysPerSecond,
    sm: config.scaleMode === 'realistic' ? 1 : 0
  };
  return `${SHARE_SCHEMA_VERSION}:${toBase64Url(JSON.stringify(payload))}`;
}

/** A finite number, else `undefined`. */
function finiteOrUndef(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Resolve a BODY_IDS index to a BodyId, or `undefined` if out of range. */
function idFromIndex(index: unknown): BodyId | undefined {
  return typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < BODY_IDS.length
    ? BODY_IDS[index]
    : undefined;
}

/**
 * Decode + STRICTLY validate a fragment against `defaults`, per-field and fail-safe
 * (AD-4). Never throws. Silent per-field clamps/fallbacks for in-payload-but-unsafe
 * values keep `ok:true`; only an unsupported version or an undecodable payload
 * returns `ok:false` (the caller surfaces the single notice for that path).
 */
export function decode(fragment: string, defaults: ShareableConfig): DecodeResult {
  try {
    // Strip a leading '#' defensively (the app already strips it, belt-and-braces).
    const clean = fragment.startsWith('#') ? fragment.slice(1) : fragment;
    const colon = clean.indexOf(':');
    const version = colon === -1 ? clean : clean.slice(0, colon);
    if (version !== String(SHARE_SCHEMA_VERSION)) {
      return { ok: false, reason: 'unsupported-version' };
    }
    const payloadStr = clean.slice(colon + 1);

    let parsed: unknown;
    try {
      parsed = JSON.parse(fromBase64Url(payloadStr));
    } catch {
      return { ok: false, reason: 'malformed' };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { ok: false, reason: 'malformed' };
    }
    const raw = parsed as Record<string, unknown>;

    // centralBody first (the a-clamp carve-out depends on it).
    const centralBody = idFromIndex(raw.c) ?? defaults.centralBody;

    // Per-body a/e. `a`: keep the CENTRAL body's value verbatim ONLY when it is exactly
    // 0 (the Sun's legitimate `a = 0`, which must survive a reversible later
    // SwitchCentre), assigning the literal 0 so a crafted `-0` normalizes to `+0` and
    // cannot break the strict-equality round-trip. Any NON-zero central `a` falls
    // through to the same clamp as every other body: only the Sun ever legitimately
    // carries `a = 0`, and a crafted `a = 0` for a non-Sun centre is inert (that body is
    // central, so excluded from collisions, and `a <= 0` skips it if it later orbits).
    // Clamping a non-zero central `a` also closes the SEC-1 sub-step footgun (a crafted
    // sub-min central `a` could otherwise drive an unbounded collision sub-step count).
    // `e`: always clamp to the editable range.
    const aArr = Array.isArray(raw.a) ? (raw.a as unknown[]) : [];
    const eArr = Array.isArray(raw.e) ? (raw.e as unknown[]) : [];
    const bodies = {} as Record<BodyId, { a: number; e: number }>;
    BODY_IDS.forEach((id, i) => {
      const aRaw = finiteOrUndef(aArr[i]);
      const eRaw = finiteOrUndef(eArr[i]);
      let a: number;
      if (aRaw === undefined) {
        a = defaults.bodies[id].a;
      } else if (id === centralBody && aRaw === 0) {
        a = 0; // carve-out: preserve the central body's a = 0 (normalizes -0 to +0).
      } else {
        a = clamp(aRaw, SEMI_MAJOR_AXIS_MIN_METRES, SEMI_MAJOR_AXIS_MAX_METRES);
      }
      const e =
        eRaw === undefined ? defaults.bodies[id].e : clamp(eRaw, ECCENTRICITY_MIN, ECCENTRICITY_MAX);
      bodies[id] = { a, e };
    });

    // legendOrder: must be an EXACT permutation of the nine BODY_IDS (mirrors the
    // reducer's ReorderLegend check); otherwise fall back to canonical order.
    const legendOrder = decodeLegendOrder(raw.l, defaults.legendOrder);

    // sectorCount: finite -> round then clamp; else default.
    const scRaw = finiteOrUndef(raw.sc);
    const sectorCount =
      scRaw === undefined
        ? defaults.sectorCount
        : clamp(Math.round(scRaw), MIN_SECTOR_COUNT, MAX_SECTOR_COUNT);

    // speed: finite -> clamp; else default.
    const spRaw = finiteOrUndef(raw.sp);
    const speedDaysPerSecond =
      spRaw === undefined
        ? defaults.speedDaysPerSecond
        : clamp(spRaw, SPEED_MIN_DAYS_PER_SECOND, SPEED_MAX_DAYS_PER_SECOND);

    // sectorOverlayVisible: strict boolean coercion accepting the 0/1 encoding.
    const sectorOverlayVisible = decodeBool(raw.so, defaults.sectorOverlayVisible);

    // scaleMode: accept the 0/1 encoding or the literal strings; else default.
    const scaleMode = decodeScaleMode(raw.sm, defaults.scaleMode);

    return {
      ok: true,
      config: {
        centralBody,
        bodies,
        legendOrder,
        sectorOverlayVisible,
        sectorCount,
        speedDaysPerSecond,
        scaleMode
      }
    };
  } catch {
    // Belt-and-braces: nothing above should throw, but AD-4 forbids ANY throw.
    return { ok: false, reason: 'malformed' };
  }
}

function decodeLegendOrder(raw: unknown, fallback: BodyId[]): BodyId[] {
  if (!Array.isArray(raw) || raw.length !== BODY_IDS.length) return [...fallback];
  const ids: BodyId[] = [];
  for (const idx of raw) {
    const id = idFromIndex(idx);
    if (id === undefined) return [...fallback];
    ids.push(id);
  }
  // Exact permutation: no duplicates (length already matches BODY_IDS).
  if (new Set(ids).size !== ids.length) return [...fallback];
  return ids;
}

function decodeBool(raw: unknown, fallback: boolean): boolean {
  if (raw === 1 || raw === true) return true;
  if (raw === 0 || raw === false) return false;
  return fallback;
}

function decodeScaleMode(raw: unknown, fallback: ScaleMode): ScaleMode {
  if (raw === 0) return 'readable';
  if (raw === 1) return 'realistic';
  if (typeof raw === 'string' && (SCALE_MODES as readonly string[]).includes(raw)) {
    return raw as ScaleMode;
  }
  return fallback;
}
